<main class="p-4 p-xxl-5">
  <div v-if="is_loading_templates" class="text-center text-muted py-5">
    Loading templates...
  </div>

  <div v-else-if="templates_error" class="alert alert-warning">
    {{ templates_error }}
  </div>

  <div v-else-if="templates.length === 0" class="text-center text-muted py-5">
    No onboarding templates are assigned to configured journeys yet.
  </div>

  <section v-else class="row g-3">
    <div
      v-for="template in templates"
      :key="template.name"
      class="col-12 col-lg-6 col-xxl-4"
    >
      <article
        class="card h-100 border rounded-3 bg-white cursor-pointer"
        tabindex="0"
        @click="open_template_detail(template)"
        @keyup.enter="open_template_detail(template)"
      >
        <div class="card-body p-4 d-flex flex-column">
          <div class="d-flex align-items-start gap-3 mb-3">
            <span
              class="rounded-2 d-inline-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0 onboarding-icon-square-22"
              :style="{ backgroundColor: template.color }"
            >
              {{ template.initial }}
            </span>
            <div class="min-w-0">
              <h2 class="h6 fw-bold text-black mb-1">{{ template.name }}</h2>
              <p class="mb-0 text-muted small">
                {{ template.type }} · {{ template.meta }}
              </p>
            </div>
          </div>

          <p class="text-muted mb-5">
            {{ template.description }}
          </p>

          <div class="d-flex align-items-center gap-2 mt-auto">
            <span class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis fw-semibold">
              {{ template.steps }}
            </span>
            <span class="badge rounded-pill bg-info-subtle text-info-emphasis fw-semibold">
              {{ template.status }}
            </span>
          </div>
        </div>
      </article>
    </div>
  </section>

  <div
    v-if="selected_template"
    class="onboarding-case-detail-backdrop"
    @click="close_template_detail"
  ></div>

  <aside
    v-if="selected_template"
    :class="['onboarding-case-detail-drawer bg-white shadow-lg', { 'is-fullscreen': is_detail_fullscreen }]"
  >
    <header class="border-bottom p-4">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <div class="text-uppercase text-muted fw-semibold small mb-3">
            {{ selected_template.journey }} journey · template
          </div>
          <h2 class="h4 fw-bold text-black mb-2">{{ selected_template.name }}</h2>
          <p class="mb-0 text-muted">{{ selected_template.description }}</p>
        </div>

        <div class="d-flex align-items-center gap-3 flex-shrink-0">
          <span class="badge rounded-pill bg-info-subtle text-info-emphasis">
            <span class="me-1">●</span>Synced to Complyon
          </span>
          <button
            type="button"
            class="btn btn-link text-muted p-0"
            :aria-label="is_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
            :title="is_detail_fullscreen ? 'Exit fullscreen' : 'View fullscreen'"
            @click="toggle_detail_fullscreen"
          >
            <i :class="['bi', is_detail_fullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen', 'fs-5']"></i>
          </button>
          <button type="button" class="btn btn-link text-muted p-0" aria-label="Close template detail" @click="close_template_detail">
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>
      </div>
    </header>

    <div class="onboarding-case-detail-body p-4">
      <dl class="row g-0 mb-4">
        <dt class="col-4 text-muted fw-normal mb-3">Journey</dt>
        <dd class="col-8 fw-semibold mb-3">
          <span class="d-inline-flex align-items-center gap-2">
            <span class="rounded-circle d-inline-block onboarding-template-dot" :style="{ backgroundColor: selected_template.color }"></span>
            <span>{{ selected_template.journey }}</span>
          </span>
        </dd>
        <dt class="col-4 text-muted fw-normal mb-3">Applies to</dt>
        <dd class="col-8 fw-semibold mb-3">{{ selected_template.applies_to }}</dd>
        <dt class="col-4 text-muted fw-normal mb-3">Owner</dt>
        <dd class="col-8 fw-semibold mb-3">{{ selected_template.owner }}</dd>
        <dt class="col-4 text-muted fw-normal mb-3">Target SLA</dt>
        <dd class="col-8 fw-semibold mb-3">{{ selected_template.target_sla }}</dd>
        <dt class="col-4 text-muted fw-normal">In use</dt>
        <dd class="col-8 fw-semibold">{{ selected_template.in_use }}</dd>
      </dl>

      <section class="row g-3 mb-4">
        <div class="col-4">
          <div class="border rounded-3 bg-light text-center p-3">
            <div class="h4 fw-bold text-black mb-2">{{ selected_template.stages }}</div>
            <div class="text-uppercase text-muted small">Stages</div>
          </div>
        </div>
        <div class="col-4">
          <div class="border rounded-3 bg-light text-center p-3">
            <div class="h4 fw-bold text-black mb-2">{{ selected_template.steps_count }}</div>
            <div class="text-uppercase text-muted small">Steps</div>
          </div>
        </div>
        <div class="col-4">
          <div class="border rounded-3 bg-light text-center p-3">
            <div class="h4 fw-bold text-black mb-2">{{ selected_template.required }}</div>
            <div class="text-uppercase text-muted small">Required</div>
          </div>
        </div>
      </section>

      <section>
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="text-uppercase text-muted fw-semibold small">Playbook</div>
          <div class="text-uppercase text-muted fw-semibold small">{{ selected_template.steps_count }} steps</div>
        </div>

        <div v-if="is_loading_template_detail" class="text-muted py-4">
          Loading playbook...
        </div>

        <div v-else-if="playbook_groups.length === 0" class="text-muted py-4">
          No backend questions are assigned to this template yet.
        </div>

        <div v-else class="d-grid gap-4">
          <section v-for="group in playbook_groups" :key="group.number">
            <div class="d-flex align-items-center gap-3 mb-3">
              <span class="badge rounded-circle bg-info-subtle text-info-emphasis d-inline-flex align-items-center justify-content-center p-0 onboarding-icon-square-155">
                {{ group.number }}
              </span>
              <h3 class="h6 fw-bold text-black mb-0">{{ group.title }}</h3>
            </div>

            <article
              v-for="item in group.items"
              :key="item.title"
              class="d-flex align-items-center gap-3 py-3 border-bottom border-light-subtle ms-4"
            >
              <span class="rounded-circle d-inline-flex align-items-center justify-content-center bg-info-subtle text-info-emphasis flex-shrink-0 onboarding-icon-square-17">
                <i :class="['bi', item.icon]"></i>
              </span>
              <div class="min-w-0 flex-grow-1">
                <div class="fw-bold text-black">{{ item.title }}</div>
                <div class="text-muted">{{ item.type }}</div>
              </div>
              <span v-if="item.required" class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis">Required</span>
            </article>
          </section>
        </div>
      </section>
    </div>

    <footer class="onboarding-case-detail-footer border-top bg-white p-3 d-flex align-items-center justify-content-between gap-3">
      <button type="button" class="btn btn-link text-muted d-inline-flex align-items-center gap-2 text-decoration-none">
        <i class="bi bi-diagram-3"></i>
        <span>Duplicate</span>
      </button>
      <div class="d-flex align-items-center gap-2">
        <button type="button" class="btn btn-outline-secondary d-inline-flex align-items-center gap-2">
          <i class="bi bi-gear"></i>
          <span>Edit playbook</span>
        </button>
        <button type="button" class="btn btn-primary d-inline-flex align-items-center gap-2">
          <i class="bi bi-plus-lg"></i>
          <span>Start onboarding</span>
        </button>
      </div>
    </footer>
  </aside>
</main>
